import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { productMessaging } from '../../config/messaging';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { user, signIn, resetPassword, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/simulations', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (showResetPassword) {
      const { error } = await resetPassword(email);
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        setResetSuccess(true);
        setLoading(false);
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        navigate('/simulations');
      }
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    const { error } = await signInWithGoogle('/simulations');
    if (error) {
      setError(error.message);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="marketing-page">
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div className="marketing-signup-content">
            <div className="marketing-signup-header">
              <h1 className="marketing-hero-title">
                {resetSuccess ? 'Check your email' : showResetPassword ? 'Reset password' : productMessaging.auth.loginTitle}
              </h1>
              <p className="marketing-hero-subtitle">
                {resetSuccess 
                  ? 'We\'ve sent you a reset link' 
                  : showResetPassword 
                  ? 'Enter your email to reset your password' 
                  : productMessaging.auth.loginSubtitle}
              </p>
              {!showResetPassword && !resetSuccess && (
                <p className="marketing-hero-description">Continue comparing assumptions, outcomes, and scenarios in one visual workspace.</p>
              )}
              {showResetPassword && !resetSuccess && (
                <p className="marketing-hero-description">We'll send you a link to reset your password</p>
              )}
              {resetSuccess && (
                <p className="marketing-hero-description">
                  We've sent a password reset link to <strong>{email}</strong>. 
                  Click the link in the email to reset your password. If you don't see it, check your spam folder.
                </p>
              )}
            </div>

            <div className="marketing-signup-form-container">
              {!resetSuccess ? (
                <>
                  {!showResetPassword && (
                    <>
                      <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={googleLoading}
                        className="marketing-btn marketing-btn-google marketing-btn-large"
                      >
                        <span className="marketing-btn-google-icon" aria-hidden="true">
                          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" focusable="false">
                            <path d="M17.64 9.2045c0-.638-.0573-1.252-.1636-1.842H9v3.481h4.84c-.2085 1.125-.8425 2.08-1.7915 2.717v2.258h2.8975c1.6965-1.5625 2.694-3.864 2.694-6.6145z" fill="#4285F4"/>
                            <path d="M9 18c2.43 0 4.4675-.806 5.9565-2.181l-2.8975-2.258c-.805.54-1.837.859-3.059.859-2.352 0-4.3455-1.588-5.0565-3.72H.957977v2.332C2.43798 15.985 5.4805 18 9 18z" fill="#34A853"/>
                            <path d="M3.9435 10.7c-.1815-.54-.2845-1.118-.2845-1.7s.103-1.16.2845-1.7V4.968H.957977A8.996 8.996 0 000 9c0 1.456.3495 2.834.957977 4.032L3.9435 10.7z" fill="#FBBC05"/>
                            <path d="M9 3.579c1.32 0 2.506.453 3.4395 1.341l2.58-2.58C13.464 1.076 11.427 0 9 0 5.4805 0 2.43798 2.015 0.957977 4.968l2.985523 2.332C4.6545 5.955 6.648 3.579 9 3.579z" fill="#EA4335"/>
                          </svg>
                        </span>
                        <span>{googleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
                      </button>
                      <div className="marketing-auth-divider">
                        <span>or</span>
                      </div>
                    </>
                  )}

                  <form onSubmit={handleSubmit} className="marketing-signup-form">
                  <div className="marketing-form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      className="marketing-form-input"
                    />
                  </div>

                  {!showResetPassword && (
                    <div className="marketing-form-group">
                      <label htmlFor="password">Password</label>
                      <input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        autoComplete="current-password"
                        className="marketing-form-input"
                      />
                    </div>
                  )}

                  {error && <div className="marketing-form-error">{error}</div>}

                    <button type="submit" disabled={loading || googleLoading} className="marketing-btn marketing-btn-primary marketing-btn-large">
                    {loading 
                      ? (showResetPassword ? 'Sending...' : 'Signing in...') 
                      : (showResetPassword ? 'Send Reset Link' : 'Continue modeling')}
                  </button>
                  </form>
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(false);
                      setResetSuccess(false);
                      setEmail('');
                      setPassword('');
                      setError(null);
                    }}
                    className="marketing-btn marketing-btn-primary marketing-btn-large"
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              {!showResetPassword && !resetSuccess && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(true);
                      setPassword('');
                      setError(null);
                    }}
                    className="marketing-nav-link"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {showResetPassword && !resetSuccess && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(false);
                      setError(null);
                    }}
                    className="marketing-nav-link"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Back to sign in
                  </button>
                </div>
              )}

              <p className="marketing-signup-footer">
                Don't have an account? <Link to="/signup" className="marketing-nav-link">Sign up</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
